import { productImageAlt } from "@/lib/imageAlt";
import { useState } from "react";
import { ShopifyProduct, formatPrice, getShopifyImageUrl } from "@/lib/shopify";
import { useCartStore } from "@/stores/cartStore";
import { defaultVariant } from "@/lib/variants";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { ShoppingCart, Minus, Plus } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

interface QuickViewModalProps {
  product: ShopifyProduct | null;
  open: boolean;
  onClose: () => void;
}

export const QuickViewModal = ({ product, open, onClose }: QuickViewModalProps) => {
  const addItem = useCartStore(state => state.addItem);
  const isLoading = useCartStore(state => state.isLoading);
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [selectedVariant, setSelectedVariant] = useState<string | null>(null);

  if (!product) return null;

  const { node } = product;
  const images = node.images?.edges || [];
  const variants = node.variants?.edges || [];
  // Same opening variant as the product page: the one the title names, in stock.
  const activeVariantId = selectedVariant || defaultVariant(node)?.id;
  const currentVariant = variants.find(v => v.node.id === activeVariantId)?.node;

  const handleAddToCart = async () => {
    if (!currentVariant) return;
    const result = await addItem({
      product,
      variantId: currentVariant.id,
      variantTitle: currentVariant.title,
      price: currentVariant.price,
      quantity,
      selectedOptions: currentVariant.selectedOptions || [],
    });
    // Sold out: the cart store has said so; stay open so another option can be picked.
    if (!result.ok) return;
    if (!result.notice) toast.success("Added to cart", { description: `${quantity}x ${node.title}`, position: "top-center" });
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      {/* The shared dialog's own Close (visible, 44px target, "Close" for screen
          readers) is the close control; hiding every direct-child button had
          hidden it and the custom X alike. The product name titles the dialog. */}
      <DialogContent className="max-w-3xl p-0 gap-0 rounded-none border-border overflow-hidden" aria-describedby={undefined}>
        <div className="grid md:grid-cols-2">
          {/* Image */}
          <div className="aspect-square bg-[hsl(0,0%,97%)] relative">
            {images[selectedImage] ? (
              <img
                src={getShopifyImageUrl(images[selectedImage].node.url, 900)}
                srcSet={`${getShopifyImageUrl(images[selectedImage].node.url, 600)} 600w, ${getShopifyImageUrl(images[selectedImage].node.url, 900)} 900w, ${getShopifyImageUrl(images[selectedImage].node.url, 1200)} 1200w`}
                sizes="(max-width: 768px) 100vw, 50vw"
                alt={productImageAlt(node.title, { index: selectedImage, total: images.length })}
                loading="eager"
                decoding="async"
                className="w-full h-full object-contain p-4"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-muted-foreground">No Image</div>
            )}
            {images.length > 1 && (
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                {images.map((_, i) => (
                  <button key={i} onClick={() => setSelectedImage(i)} aria-label={`Photo ${i + 1} of ${images.length}`} className={`w-2 h-2 rounded-full transition-colors ${i === selectedImage ? "bg-foreground" : "bg-border"}`} />
                ))}
              </div>
            )}
          </div>

          {/* Details */}
          <div className="p-6 flex flex-col">
            <DialogTitle asChild>
              <Link to={`/product/${node.handle}`} onClick={onClose} className="font-display text-xl leading-snug tracking-normal font-semibold text-foreground hover:text-primary transition-colors mb-1">
                {node.title}
              </Link>
            </DialogTitle>

            {currentVariant && (
              <p className="text-lg font-medium text-foreground mb-4">
                {formatPrice(currentVariant.price.amount, currentVariant.price.currencyCode)}
              </p>
            )}

            {node.description && (
              <p className="text-sm text-muted-foreground leading-relaxed mb-4 line-clamp-3">{node.description}</p>
            )}

            {/* Variants */}
            {variants.length > 1 && (
              <div className="mb-4">
                <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground mb-2 block">Options</span>
                <div className="flex flex-wrap gap-1.5">
                  {variants.map((v) => (
                    <button
                      key={v.node.id}
                      onClick={() => setSelectedVariant(v.node.id)}
                      disabled={!v.node.availableForSale}
                      className={`px-3 py-1.5 border text-xs font-medium transition-colors ${
                        activeVariantId === v.node.id
                          ? "border-foreground bg-foreground text-background"
                          : v.node.availableForSale
                          ? "border-border hover:border-foreground"
                          : "border-border opacity-40 cursor-not-allowed"
                      }`}
                    >
                      {v.node.title}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Quantity */}
            <div className="flex items-center gap-3 mb-4">
              <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">Qty</span>
              <div className="flex items-center border border-border">
                <button onClick={() => setQuantity(Math.max(1, quantity - 1))} className="h-8 w-8 flex items-center justify-center hover:bg-muted transition-colors">
                  <Minus className="h-3 w-3" />
                </button>
                <span className="w-8 text-center text-sm font-medium">{quantity}</span>
                <button onClick={() => setQuantity(quantity + 1)} className="h-8 w-8 flex items-center justify-center hover:bg-muted transition-colors">
                  <Plus className="h-3 w-3" />
                </button>
              </div>
            </div>

            <div className="mt-auto space-y-2">
              <Button
                onClick={handleAddToCart}
                className="w-full bg-foreground text-background hover:bg-foreground/90 rounded-none text-[12px] tracking-widest uppercase py-5 font-medium"
                disabled={!currentVariant?.availableForSale || isLoading}
              >
                <ShoppingCart className="mr-2 h-4 w-4" />
                Add to Cart
              </Button>
              <Link to={`/product/${node.handle}`} onClick={onClose} className="block text-center text-xs text-muted-foreground hover:text-foreground transition-colors py-1">
                View Full Details →
              </Link>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
